<template>
  <Dialog
    v-model:visible="visibleProxy"
    modal
    appendTo="body"
    :baseZIndex="baseZIndex"
    :closable="!saving"
    :closeOnEscape="!saving"
    :dismissableMask="!saving"
    :draggable="false"
    :style="{ width: '28rem' }"
    :breakpoints="{ '960px': '90vw', '575px': '98vw' }"
    :pt="crcFormDialogPt"
    @hide="onClose"
  >
    <template #header>
      <div class="crc-form-dialog-header">
        <div class="crc-form-dialog-header__icon bg-emerald-50 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
          <i class="pi pi-globe text-base" />
        </div>
        <div class="crc-form-dialog-header__text">
          <div class="crc-form-dialog-header__title">
            {{ modalTitle }}
          </div>
        </div>
      </div>
    </template>

    <form @submit.prevent="handleSubmit">
      <Message v-if="saveError" severity="error" :closable="false" class="mb-4" role="alert">
        {{ saveError.message }}
      </Message>
      <section class="crc-form-dialog-section crc-form-dialog-fields">
        <FloatLabel variant="on" class="w-full">
          <InputText
            id="country-edit-name"
            v-model="draft.name"
            :disabled="saving"
            maxlength="80"
            autocomplete="off"
            :invalid="submitted && !draft.name?.trim()"
            class="w-full rounded-md"
          />
          <label for="country-edit-name" class="text-sm font-medium">
            Country <span class="text-red-500">*</span>
          </label>
        </FloatLabel>
        <p class="country-edit-modal__hint">
          {{ hint }}
        </p>
      </section>

      <div class="crc-form-dialog-footer">
        <div class="crc-form-dialog-footer__actions">
          <Button
            type="button"
            label="Cancel"
            severity="secondary"
            text
            :disabled="saving"
            @click="visibleProxy = false"
          />
          <Button type="submit" label="Save" icon="pi pi-check" :loading="saving" :disabled="saving" />
        </div>
      </div>
    </form>
  </Dialog>
</template>

<script setup>
import { useSettingsDraftGuard } from '@/features/settings/composables/useSettingsDraftGuard';
import { computed, reactive, ref } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Message from 'primevue/message';
import FloatLabel from 'primevue/floatlabel';
import InputText from 'primevue/inputtext';
import { crcFormDialogPt } from '@/components/common/dialog/crcDialogPt';
import { countryOptionLabel } from '@/utils/countryCatalog';

defineOptions({ name: 'CountryEditModal' });

const props = defineProps({
  saveError: { type: Object, default: null },
  visible: {
    type: Boolean,
    default: false,
  },
  saving: {
    type: Boolean,
    default: false,
  },
  baseZIndex: {
    type: Number,
    default: 1200,
  },
});

const emit = defineEmits(['update:visible', 'save']);

const submitted = ref(false);
const draft = reactive({
  id: null,
  name: '',
});

const visibleProxy = computed({
  get: () => props.visible,
  set: (value) => {
    if (value) emit('update:visible', true);
    else if (props.visible) draftGuard.requestLeave(() => emit('update:visible', false));
  },
});

const isNew = computed(() => draft.id == null);
const modalTitle = computed(() => (isNew.value ? 'Add Country' : 'Edit Country'));
const hint = computed(() => (
  isNew.value
    ? 'The country becomes available for work sites and employee profiles.'
    : 'Renaming updates work sites and employee profiles that use this country. US and UK stay fixed.'
));

const resetDraft = () => {
  draft.id = null;
  draft.name = '';
  submitted.value = false;
};

const openNew = () => {
  resetDraft();
};

const openEdit = (country) => {
  draft.id = country?.id ?? null;
  draft.name = countryOptionLabel(country?.name) || country?.label || '';
  submitted.value = false;
};

const onClose = () => {
  if (!props.saving) resetDraft();
};

const handleSubmit = () => {
  submitted.value = true;
  if (!draft.name?.trim() || props.saving) return;
  if (isNew.value) {
    emit('save', { mode: 'new', payload: { name: draft.name.trim() } });
    return;
  }
  emit('save', {
    mode: 'edit',
    payload: { country_id: draft.id, name: draft.name.trim() },
  });
};

defineExpose({ openNew, openEdit });
const draftGuard = useSettingsDraftGuard({
  draft, visible: () => props.visible, saving: () => props.saving,
});
</script>

<style scoped>
.country-edit-modal__hint {
  margin: 0.25rem 0 0;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: var(--p-text-muted-color);
}
</style>
